/**
 * /kit — tutti i componenti del design system in una pagina, con i controlli di
 * scala del testo, alto contrasto e movimento ridotto. Serve a noi e agli screenshot
 * (scripts/screenshots.mjs), non a lei: non e raggiungibile dalla navigazione dell'app.
 *
 * Stato iniziale da query string, cosi ogni screenshot e riproducibile:
 *   /kit?scala=1.4&contrasto=alto&movimento=ridotto
 */
import type { Accessibility } from '@mamma/contracts'
import type { ReactNode } from 'react'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'

import {
  AnswerField,
  AskAgentLink,
  ChoiceChip,
  ConfirmFactSheet,
  ConsentCard,
  ErrorMessage,
  PrimaryButton,
  ProgressWords,
  QuestionTitle,
  ReminderRow,
  SafetyBanner,
  SecondaryButton,
  SummarySection,
  TEXT_SCALES,
  WhyText,
  WizardCard,
  applyAccessibility,
} from '../components'
import { isTextScale } from '../components/accessibility'
import { CARD_MOCK } from '../mock'
import { type ContrastReport, measureElement } from './contrast'

/** Soglia dei token: testo >= 7:1 (DESIGN-TOKENS §2). */
const TEXT_THRESHOLD = 7

function prefsFromQuery(): Accessibility {
  const q = new URLSearchParams(window.location.search)
  const scale = Number(q.get('scala') ?? '1')
  return {
    text_scale: isTextScale(scale) ? scale : 1,
    high_contrast: q.get('contrasto') === 'alto',
    reduce_motion: q.get('movimento') === 'ridotto',
  }
}

function syncQuery(prefs: Accessibility) {
  const q = new URLSearchParams()
  if (prefs.text_scale !== 1) q.set('scala', String(prefs.text_scale))
  if (prefs.high_contrast) q.set('contrasto', 'alto')
  if (prefs.reduce_motion) q.set('movimento', 'ridotto')
  const search = q.toString()
  window.history.replaceState(null, '', `${window.location.pathname}${search ? `?${search}` : ''}`)
}

export function KitPage() {
  // Applicate prima del primo render: altrimenti i componenti nascono coi token di
  // default e le transizioni partono verso la modalita scelta.
  const [prefs, setPrefs] = useState<Accessibility>(() => {
    const initial = prefsFromQuery()
    applyAccessibility(initial)
    return initial
  })
  const [chip, setChip] = useState<string | undefined>('Verso le 8')
  const [sheetOpen, setSheetOpen] = useState(false)
  const [contrast, setContrast] = useState<ContrastReport | null>(null)
  const probeRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    applyAccessibility(prefs)
    syncQuery(prefs)
  }, [prefs])

  // Misura a transizioni concluse: durante il cambio di modalita i colori calcolati
  // sono intermedi e il valore sarebbe falso.
  useEffect(() => {
    const button = probeRef.current?.querySelector('[data-component="primary-button"]')
    if (!button) return
    let cancelled = false
    setContrast(null)
    Promise.all(button.getAnimations().map((a) => a.finished.catch(() => undefined))).then(() => {
      if (!cancelled) setContrast(measureElement(button))
    })
    return () => {
      cancelled = true
    }
  }, [prefs])

  const set = (patch: Partial<Accessibility>) => setPrefs((p) => ({ ...p, ...patch }))

  return (
    <div className="mx-auto w-full max-w-[430px] px-[20px] pb-[40px] pt-[24px] text-ink">
      <h1 className="text-title" style={{ fontWeight: 'var(--type-title-weight)' as never }}>
        Componenti
      </h1>
      <p className="text-soft mt-1 text-meta">Direzione 03 Aria · fallback alto contrasto 04 Chiara</p>

      <section aria-label="Controlli" className="mt-5 flex flex-col gap-3 rounded-field border-2 border-line p-4">
        <p className="text-meta font-bold">Grandezza del testo</p>
        <div className="flex flex-wrap gap-2">
          {TEXT_SCALES.map((s) => (
            <ChoiceChip
              key={s}
              label={`×${String(s).replace('.', ',')}`}
              selected={prefs.text_scale === s}
              onSelect={() => set({ text_scale: s })}
            />
          ))}
        </div>
        <Toggle
          label="Alto contrasto"
          checked={prefs.high_contrast}
          onChange={(v) => set({ high_contrast: v })}
        />
        <Toggle
          label="Riduci il movimento"
          checked={prefs.reduce_motion}
          onChange={(v) => set({ reduce_motion: v })}
        />
      </section>

      <Section name="Contrasto del pulsante primario">
        <div ref={probeRef}>
          <PrimaryButton>Invia</PrimaryButton>
        </div>
        {contrast && (
          <div data-testid="contrast-report" className="mt-3 text-meta">
            <p>
              Testo {contrast.text} · scala ×{String(prefs.text_scale).replace('.', ',')}
              {prefs.high_contrast ? ' · alto contrasto' : ''}
            </p>
            {contrast.stops.map((s) => (
              <p key={s.color}>
                Fondo {s.color}: {s.ratio.toFixed(2)}:1
              </p>
            ))}
            <p className="font-bold" data-testid="contrast-worst">
              Peggiore {contrast.worst.ratio.toFixed(2)}:1 su {contrast.worst.color} ·{' '}
              {contrast.worst.ratio >= TEXT_THRESHOLD ? 'passa la soglia 7:1' : 'sotto la soglia 7:1'}
            </p>
          </div>
        )}
      </Section>

      <Section name="WizardCard">
        <div className="-mx-[20px] rounded-card border-2 border-dashed border-line">
          <WizardCard
            {...CARD_MOCK}
            draftKey="kit:wizard"
            selectedChoice={chip}
            onChoice={setChip}
          />
        </div>
      </Section>

      <Section name="ProgressWords">
        <ProgressWords block="Le sue giornate" progress={{ current: 3, total: 12 }} />
      </Section>

      <Section name="QuestionTitle · WhyText">
        <QuestionTitle as="h2">Chi la accompagna di solito dal medico?</QuestionTitle>
        <WhyText>Così, se serve, so a chi mandare il promemoria dell’appuntamento.</WhyText>
      </Section>

      <Section name="ChoiceChip">
        <div className="flex flex-wrap gap-x-2 gap-y-[10px]">
          <ChoiceChip label="Non scelta" />
          <ChoiceChip label="Scelta" selected />
          <ChoiceChip label="Non disponibile" disabled />
        </div>
      </Section>

      <Section name="AnswerField">
        <AnswerField label="Oppure scriva qui" draftKey="kit:campo" />
        <p className="text-soft mt-2 text-meta">Il testo resta qui anche se ricarica la pagina.</p>
      </Section>

      <Section name="PrimaryButton · SecondaryButton · AskAgentLink">
        <PrimaryButton>Invia</PrimaryButton>
        <div className="mt-3 flex flex-wrap gap-2.5">
          <SecondaryButton grow>Salta</SecondaryButton>
          <SecondaryButton grow>Non so</SecondaryButton>
          <SecondaryButton grow>Non ora</SecondaryButton>
        </div>
        <AskAgentLink label="Chiedi a mia Tina" />
      </Section>

      <Section name="ConsentCard">
        <div className="flex flex-col gap-4">
          <ConsentCard
            scope="reminder_whatsapp"
            title="Promemoria su WhatsApp"
            description="Le mando i promemoria anche su WhatsApp, al numero che mi ha dato. Nessun altro li riceve."
            granted={false}
          />
          <ConsentCard
            scope="profilo"
            title="Ricordare le sue abitudini"
            description="Tengo a mente orari e preferenze che mi racconta, per non chiederle le stesse cose due volte."
            granted
          />
        </div>
      </Section>

      <Section name="ConfirmFactSheet">
        <ConfirmFactSheet presentation="inline" statement="si sveglia verso le 7" />
        <div className="mt-3">
          <SecondaryButton onClick={() => setSheetOpen(true)}>Apri come foglio</SecondaryButton>
        </div>
        {sheetOpen && (
          <ConfirmFactSheet
            statement="si sveglia verso le 7"
            onYes={() => setSheetOpen(false)}
            onNo={() => setSheetOpen(false)}
            onCorrect={() => setSheetOpen(false)}
          />
        )}
      </Section>

      <Section name="SummarySection">
        <SummarySection
          title="Le sue giornate"
          items={[
            { label: 'Sveglia', value: 'Verso le 7' },
            { label: 'Pranzo', value: 'All’una, di solito a casa' },
            { label: 'Riposo del pomeriggio' },
          ]}
          onEdit={() => undefined}
        />
      </Section>

      <Section name="ReminderRow">
        <div className="flex flex-col gap-3">
          <ReminderRow time="8:30" title="Medicina del mattino" kind="farmaco" status="confermato" channels={['in_app']} />
          <ReminderRow
            time="10:00"
            title="Visita dal dentista"
            kind="appuntamento"
            status="programmato"
            channels={['in_app', 'whatsapp']}
          />
          <ReminderRow time="18:00" title="Bolletta della luce" kind="scadenza" status="fallito" channels={['whatsapp']} />
        </div>
      </Section>

      <Section name="SafetyBanner">
        <SafetyBanner
          message="Mi fermo un momento: quello che ha scritto è importante. Se sta male adesso, chiami il suo medico o una persona vicina."
          actionLabel="Ho capito"
        />
      </Section>

      <Section name="ErrorMessage">
        <ErrorMessage
          error={{
            code: 'rete_assente',
            message_it: 'La risposta non è arrivata: la connessione si è interrotta.',
            remedy_it: 'Il testo è salvato su questo telefono. Tocchi Riprova quando la connessione torna.',
          }}
          onRetry={() => undefined}
        />
      </Section>
    </div>
  )
}

function Section({ name, children }: { name: string; children: ReactNode }) {
  return (
    <section className="mt-8">
      <h2 className="mb-3 border-b-2 border-line pb-1 text-meta font-bold">{name}</h2>
      {children}
    </section>
  )
}

function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className="flex min-h-touch w-full items-center justify-between gap-3 rounded-control border-2 border-line px-4 py-2 text-left text-body"
    >
      <span>{label}</span>
      <span className="shrink-0 font-bold">{checked ? 'Attivo' : 'Spento'}</span>
    </button>
  )
}
